import { avatarConfig } from "@repo/ui/branded";
import React from "react";
import Avatar, { AvatarProps } from "./index";

const marginMap = {
  large: "-14px",
  medium: "-10px",
  small: "-10px",
};

const sizeMap = {
  large: 80,
  medium: 48,
  small: 40,
};

const widthMap = {
  assigned: {
    small: "68px",
    medium: "84px",
    large: "130px",
  },
  notAssigned: {
    small: "104px",
    medium: "120px",
    large: "166px",
  },
};

export interface AvatarGroupProps {
  /** The first avatar variant */
  firstAvatar: AvatarProps["variant"];
  /** The first avatar's custom image URL (if variant is "custom") */
  firstAvatarUrl?: string;
  /** The first avatar's initial letter (if variant is "initial") */
  firstAvatarInitial?: string;
  /** The second avatar variant */
  secondAvatar: AvatarProps["variant"];
  /** The second avatar's custom image URL (if variant is "custom") */
  secondAvatarUrl?: string;
  /** The second avatar's initial letter (if variant is "initial") */
  secondAvatarInitial?: string;
  /** The size of both avatars */
  size?: "small" | "medium" | "large";
  /** Whether the avatars are assigned (overlapping) or not (separated by line) */
  state: "assigned" | "not-assigned";
  /** Layout: "overlap" stacks horizontally; "diagonal" offsets the second avatar down-and-right. Only applies when state is "assigned". */
  layout?: "overlap" | "diagonal";
  /** Optional className for additional styling */
  className?: string;
}

const AvatarGroup: React.FC<AvatarGroupProps> = ({
  firstAvatar,
  firstAvatarUrl,
  firstAvatarInitial,
  secondAvatar,
  secondAvatarUrl,
  secondAvatarInitial,
  size = "medium",
  state,
  layout = "overlap",
  className = "",
}) => {
  const isAssigned = state === "assigned";

  if (isAssigned && layout === "diagonal") {
    const avatarSize = sizeMap[size];
    const offset = Math.round(avatarSize * 0.4);
    const containerSize = avatarSize + offset;

    // Diagonal stack (per Owlet design): the patient (first) avatar sits
    // behind at the top-left; the clinician (second) is offset down-and-right.
    // Per Figma the clinician carries a 1px separator the colour of the card
    // surface so it reads as a gap where the two overlap. We draw it with an
    // *inset* ring (ring-inset) rather than an outer ring so both avatars keep
    // the same rendered footprint — an outer ring would make the front avatar
    // visibly larger than the back.
    return (
      <div
        className={`relative ${className}`}
        style={{ width: containerSize, height: containerSize }}
      >
        <div className="absolute top-0 left-0" style={{ lineHeight: 0 }}>
          <Avatar
            variant={firstAvatar}
            imageUrl={firstAvatarUrl}
            initialText={firstAvatarInitial}
            size={size}
          />
        </div>
        <div
          className="absolute rounded-full ring-1 ring-inset ring-surface"
          style={{ top: offset, left: offset, lineHeight: 0 }}
        >
          <Avatar
            variant={secondAvatar}
            imageUrl={secondAvatarUrl}
            initialText={secondAvatarInitial}
            size={size}
          />
        </div>
      </div>
    );
  }
  const containerWidth = isAssigned
    ? widthMap.assigned[size]
    : widthMap.notAssigned[size];
  const containerHeight = `${sizeMap[size]}px`;
  const animationWidth = isAssigned ? "0px" : "22px";

  return (
    <div
      className={`flex justify-between items-center ${className}`}
      style={{
        width: containerWidth,
        height: containerHeight,
        padding: 0,
      }}
    >
      {/* First Avatar */}
      <div className="shrink-0" style={{ lineHeight: 0 }}>
        <Avatar
          variant={firstAvatar}
          imageUrl={firstAvatarUrl}
          initialText={firstAvatarInitial}
          size={size}
        />
      </div>

      {/* Animation Line Container */}
      <div
        className="transition-all duration-300 ease-in-out flex items-center"
        style={{
          width: animationWidth,
          position: "relative",
          overflow: "hidden",
          height: containerHeight,
          opacity: isAssigned ? 0 : 1,
          transform: isAssigned
            ? `translateX(${marginMap[size]})`
            : "translateX(0)",
        }}
      >
        <div
          style={{
            width: "16px",
            height: "2px",
            backgroundColor: "var(--avatar-group-loader, #CD83D4)",
            borderRadius: "10px",
            margin: "0 auto",
            animation: isAssigned
              ? "none"
              : "bounceLeftRight 1s linear infinite alternate-reverse",
          }}
        />
      </div>

      {/* Second Avatar */}
      <div
        className={`shrink-0 transition-all duration-300 ease-in-out ${
          isAssigned && avatarConfig.ringSecondAvatarWhenAssigned
            ? "rounded-full ring-1 ring-inset ring-surface"
            : ""
        }`}
        style={{
          transform: isAssigned
            ? `translateX(${marginMap[size]})`
            : "translateX(0)",
          lineHeight: 0,
        }}
      >
        <Avatar
          variant={secondAvatar}
          imageUrl={secondAvatarUrl}
          initialText={secondAvatarInitial}
          size={size}
        />
      </div>

      {/* Animation Keyframes */}
      <style>
        {`
          @keyframes bounceLeftRight {
            0% {
              transform: translateX(-8px) scaleX(0.5);
            }
            50% {
              transform: translateX(0) scaleX(1);
            }
            100% {
              transform: translateX(8px) scaleX(0.5);
            }
          }
        `}
      </style>
    </div>
  );
};

export default AvatarGroup;
